import React from 'react';
import {
  ChevronLeft, ChevronRight, Truck, Package, CheckCircle, Clock, CreditCard, AlertCircle,
  MapPin, Calendar, DollarSign, Receipt, Download,
  ShoppingCart
} from 'lucide-react';
import { DELIVERY_STATUS, DELIVERY_STATUS_LABELS, DELIVERY_STATUS_COLORS, ORDER_STATUS_LABELS } from '../constants/status';

// 状态图标映射
const STATUS_ICONS = {
  [DELIVERY_STATUS.WAITING_DELIVERY]: Truck,
  [DELIVERY_STATUS.ARRIVED]: MapPin,
  [DELIVERY_STATUS.WAITING_APPROVAL]: AlertCircle,
  [DELIVERY_STATUS.STORED]: Package,
  [DELIVERY_STATUS.PENDING_SETTLEMENT]: DollarSign,
  [DELIVERY_STATUS.SETTLED]: CheckCircle,
  [DELIVERY_STATUS.INVOICED]: Receipt,
  [DELIVERY_STATUS.PAID]: CreditCard,
};

// 时间线状态颜色
const TIMELINE_COLORS = {
  [DELIVERY_STATUS.WAITING_DELIVERY]: 'bg-slate-500',
  [DELIVERY_STATUS.ARRIVED]: 'bg-blue-500',
  [DELIVERY_STATUS.WAITING_APPROVAL]: 'bg-amber-500',
  [DELIVERY_STATUS.STORED]: 'bg-purple-500',
  [DELIVERY_STATUS.PENDING_SETTLEMENT]: 'bg-orange-500',
  [DELIVERY_STATUS.SETTLED]: 'bg-amber-500',
  [DELIVERY_STATUS.INVOICED]: 'bg-teal-500',
  [DELIVERY_STATUS.PAID]: 'bg-green-500',
};

// 流程阶段定义（不含 WAITING_APPROVAL，这是异常状态）
const FLOW_STAGES = [
  { key: 'reservation', label: '预到货通知', status: DELIVERY_STATUS.WAITING_DELIVERY, icon: Calendar, color: 'slate' },
  { key: 'arrival', label: '到场确认', status: DELIVERY_STATUS.ARRIVED, icon: MapPin, color: 'blue' },
  { key: 'storage', label: '入库过磅', status: DELIVERY_STATUS.STORED, icon: Package, color: 'purple' },
  { key: 'settlement', label: '结算确认', status: DELIVERY_STATUS.SETTLED, icon: DollarSign, color: 'orange' },
  { key: 'invoice', label: '发票开具', status: DELIVERY_STATUS.INVOICED, icon: Receipt, color: 'teal' },
  { key: 'payment', label: '付款完成', status: DELIVERY_STATUS.PAID, icon: CreditCard, color: 'green' },
];

// 获取阶段状态：completed / active / pending
const getStageState = (currentStatus, stageStatus) => {
  const statusOrder = [
    DELIVERY_STATUS.WAITING_DELIVERY,
    DELIVERY_STATUS.ARRIVED,
    DELIVERY_STATUS.WAITING_APPROVAL,
    DELIVERY_STATUS.STORED,
    DELIVERY_STATUS.PENDING_SETTLEMENT,
    DELIVERY_STATUS.SETTLED,
    DELIVERY_STATUS.INVOICED,
    DELIVERY_STATUS.PAID,
  ];
  const currentIndex = statusOrder.indexOf(currentStatus);
  const stageIndex = statusOrder.indexOf(stageStatus);

  // WAITING_APPROVAL 是异常状态，特殊处理
  if (currentStatus === DELIVERY_STATUS.WAITING_APPROVAL) {
    if (stageStatus === DELIVERY_STATUS.ARRIVED) return 'active';
    if (stageIndex < statusOrder.indexOf(DELIVERY_STATUS.ARRIVED)) return 'completed';
    return 'pending';
  }

  if (currentStatus === stageStatus) return 'active';
  if (currentIndex > stageIndex) return 'completed';
  return 'pending';
};

// 进度条组件
const ProgressSteps = ({ currentStatus }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        {FLOW_STAGES.map((stage, index) => {
          const stageState = getStageState(currentStatus, stage.status);
          const Icon = stage.icon;
          const isLast = index === FLOW_STAGES.length - 1;

          const stateStyles = {
            completed: {
              icon: 'bg-green-500 text-white',
              line: 'bg-green-500',
              text: 'text-green-600',
            },
            active: {
              icon: `bg-${stage.color}-500 text-white ring-4 ring-${stage.color}-100`,
              line: 'bg-slate-200',
              text: `text-${stage.color}-600 font-medium`,
            },
            pending: {
              icon: 'bg-slate-200 text-slate-400',
              line: 'bg-slate-200',
              text: 'text-slate-400',
            },
          };

          const style = stateStyles[stageState];

          return (
            <div key={stage.key} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  stageState === 'active' ? `bg-${stage.color}-500 text-white ring-4 ring-${stage.color}-100` : ''
                } ${stageState === 'completed' ? 'bg-green-500 text-white' : ''} ${
                  stageState === 'pending' ? 'bg-slate-200 text-slate-400' : ''
                }`}>
                  {stageState === 'completed' ? (
                    <CheckCircle size={20} />
                  ) : (
                    <Icon size={18} />
                  )}
                </div>
                <span className={`text-xs mt-2 ${style.text}`}>{stage.label}</span>
              </div>
              {!isLast && (
                <div className={`flex-1 h-0.5 mx-2 mb-6 ${stageState === 'completed' ? 'bg-green-500' : 'bg-slate-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 阶段信息卡片组件
const StageCard = ({ title, icon: Icon, status, children, stageData, onClick, clickable }) => {
  const isCompleted = !!stageData;
  const isPending = !stageData;
  const canClick = clickable && isCompleted && onClick;

  return (
    <div
      onClick={canClick ? onClick : undefined}
      className={`bg-white rounded-xl border overflow-hidden transition-all ${
        isCompleted ? 'border-slate-200' : 'border-slate-100 border-dashed'
      } ${canClick ? 'cursor-pointer hover:border-blue-300 hover:shadow-md' : ''}`}
    >
      {/* 标题栏 */}
      <div className={`flex items-center justify-between px-4 py-3 ${
        isCompleted ? 'bg-slate-50 border-b border-slate-200' : 'bg-slate-50/50'
      }`}>
        <div className="flex items-center gap-2">
          <Icon size={16} className={isCompleted ? 'text-slate-500' : 'text-slate-300'} />
          <h3 className={`font-medium ${isCompleted ? 'text-slate-700' : 'text-slate-400'}`}>{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {canClick && (
            <span className="text-xs text-blue-500 flex items-center gap-1">
              查看详情 <ChevronRight size={12} />
            </span>
          )}
          <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${
            isCompleted
              ? 'bg-green-100 text-green-600'
              : 'bg-slate-100 text-slate-400'
          }`}>
            {isCompleted ? (
              <>
                <CheckCircle size={12} />
                <span>已完成</span>
              </>
            ) : (
              <>
                <Clock size={12} />
                <span>待处理</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 内容区 */}
      <div className={`p-4 ${isPending ? 'opacity-50' : ''}`}>
        {children}
      </div>
    </div>
  );
};

// 信息行组件
const InfoRow = ({ label, value, className = '', highlight = false }) => (
  <div className={`flex justify-between items-center py-1.5 ${className}`}>
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`text-sm ${highlight ? 'font-bold text-slate-800' : 'font-medium text-slate-700'}`}>
      {value || '-'}
    </span>
  </div>
);

// 主组件
export default function DeliveryDetailView({
  delivery,
  order,
  onBack,
  onConfirmReservation,
  onConfirmArrival,
  onStore,
  onViewArrivalNotice,
  onViewStockin,
  onViewSettlement,
  onViewInvoice,
  onViewPayment,
}) {
  if (!delivery) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">送货单不存在</p>
      </div>
    );
  }

  const statusConfig = DELIVERY_STATUS_COLORS[delivery.status];
  const StatusIcon = STATUS_ICONS[delivery.status];
  const isWaitingApproval = delivery.status === DELIVERY_STATUS.WAITING_APPROVAL;

  return (
    <div className="space-y-6">
      {/* 返回按钮和标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} className="text-slate-600" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-800">送货单详情</h2>
          <p className="text-sm text-slate-500">{delivery.deliveryNo}</p>
        </div>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${statusConfig.bg}`}>
          <StatusIcon size={18} className={statusConfig.text} />
          <span className={`font-medium ${statusConfig.text}`}>
            {DELIVERY_STATUS_LABELS[delivery.status]}
          </span>
        </div>
      </div>

      {/* 等待审批提示 */}
      {isWaitingApproval && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-amber-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">车辆等待订单审批</h4>
              <p className="text-sm text-amber-700 mt-1">
                车辆已于 {delivery.arrival?.at} 到达基地，但关联订单尚未审批通过。
                请先完成订单审批，审批通过后可进行入库操作。
              </p>
              <div className="mt-2 text-sm">
                <span className="text-amber-600">关联订单: </span>
                <span className="font-medium text-amber-800">{delivery.orderNo}</span>
                <span className="mx-2 text-amber-400">|</span>
                <span className="text-amber-600">订单状态: </span>
                <span className="font-medium text-amber-800">
                  {ORDER_STATUS_LABELS.workbench[order?.status] || order?.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 进度条 */}
      <ProgressSteps currentStatus={delivery.status} />

      {/* 主要内容 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左侧 - 信息卡片 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 关联信息 */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
              <ShoppingCart size={16} className="text-slate-500" />
              <h3 className="font-medium text-slate-700">关联信息</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">订单编号</p>
                  <p className="text-sm font-medium text-slate-800">{delivery.orderNo}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">订单状态</p>
                  <p className="text-sm font-medium text-slate-800">
                    {ORDER_STATUS_LABELS.workbench[order?.status]}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">供应商</p>
                  <p className="text-sm font-medium text-slate-800">{delivery.supplierName}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">物料</p>
                  <p className="text-sm font-medium text-slate-800">{delivery.materialName}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">基地</p>
                  <p className="text-sm font-medium text-slate-800">{delivery.baseName}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">基地地址</p>
                  <p className="text-sm font-medium text-slate-800">{delivery.baseAddress || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 阶段信息卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 预到货通知 */}
            <StageCard
              title="预到货通知"
              icon={Calendar}
              stageData={delivery.reservation}
              onClick={() => onViewArrivalNotice?.(delivery)}
              clickable={!!onViewArrivalNotice}
            >
              <div className="space-y-1">
                <InfoRow label="预约时间" value={delivery.reservation?.time} />
                <InfoRow label="车牌号" value={delivery.reservation?.vehicleNo} />
                <InfoRow label="司机姓名" value={delivery.reservation?.driverName} />
                <InfoRow label="司机电话" value={delivery.reservation?.driverPhone} />
              </div>
            </StageCard>

            {/* 到场确认 */}
            <StageCard
              title="到场确认"
              icon={MapPin}
              stageData={delivery.arrival}
            >
              {delivery.arrival ? (
                <div className="space-y-1">
                  <InfoRow label="到场时间" value={delivery.arrival.at} />
                  <InfoRow label="确认人" value={delivery.arrival.operator} />
                  {delivery.arrival.photo && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">到场照片</p>
                      <div className="w-24 h-16 bg-slate-100 rounded flex items-center justify-center">
                        <span className="text-[10px] text-slate-400">{delivery.arrival.photo}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm">
                  等待车辆到场
                </div>
              )}
            </StageCard>

            {/* 入库过磅 */}
            <StageCard
              title="入库过磅"
              icon={Package}
              stageData={delivery.storage}
              onClick={() => onViewStockin?.(delivery)}
              clickable={!!onViewStockin}
            >
              {delivery.storage ? (
                <div>
                  {/* 入库单号和金额 */}
                  <div className="mb-3 p-2 bg-purple-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-[10px] text-purple-500">入库单号</p>
                        <p className="text-sm font-medium text-purple-700">{delivery.storage.stockInNo}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-purple-500">含税金额</p>
                        <p className="text-lg font-bold text-purple-700">
                          ¥{(delivery.storage.amountWithTax || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 重量信息 */}
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">毛重</span>
                      <span className="text-slate-700">{delivery.storage.grossWeight} 吨</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">皮重</span>
                      <span className="text-slate-700">{delivery.storage.tareWeight} 吨</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">净重</span>
                      <span className="text-slate-700">{delivery.storage.netWeight} 吨</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">扣杂</span>
                      <span className="text-slate-700">{delivery.storage.deduction} 吨</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-500">实重</span>
                      <span className="font-bold text-slate-800">{delivery.storage.actualWeight} 吨</span>
                    </div>
                  </div>

                  <div className="mt-3 pt-2 border-t border-slate-100 flex justify-between text-xs text-slate-500">
                    <span>质检: {delivery.storage.quality || '-'}</span>
                    <span>{delivery.storage.at}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm">
                  等待入库过磅
                </div>
              )}
            </StageCard>

            {/* 结算确认 */}
            <StageCard
              title="结算确认"
              icon={DollarSign}
              stageData={delivery.settlement}
              onClick={() => onViewSettlement?.(delivery)}
              clickable={!!onViewSettlement}
            >
              {delivery.settlement ? (
                <div>
                  {/* 结算金额 */}
                  <div className="mb-3 p-2 bg-orange-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-[10px] text-orange-500">结算金额</p>
                        <p className="text-lg font-bold text-orange-700">
                          ¥{(delivery.settlement.amount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-orange-500">单价</p>
                        <p className="text-sm font-medium text-orange-700">
                          ¥{delivery.settlement.unitPrice}/吨
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <InfoRow label="结算单号" value={delivery.settlement.settlementNo} />
                    <InfoRow
                      label="供应商确认"
                      value={delivery.settlement.confirmedBySupplier ? '已确认' : '待确认'}
                      highlight={delivery.settlement.confirmedBySupplier}
                    />
                    {delivery.settlement.confirmedAt && (
                      <InfoRow label="确认时间" value={delivery.settlement.confirmedAt} />
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm">
                  等待结算确认
                </div>
              )}
            </StageCard>

            {/* 发票开具 */}
            <StageCard
              title="发票开具"
              icon={Receipt}
              stageData={delivery.invoice}
              onClick={() => onViewInvoice?.(delivery)}
              clickable={!!onViewInvoice}
            >
              {delivery.invoice ? (
                <div>
                  {/* 发票金额 */}
                  <div className="mb-3 p-2 bg-teal-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-[10px] text-teal-500">发票金额</p>
                        <p className="text-lg font-bold text-teal-700">
                          ¥{(delivery.invoice.amount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-teal-500">发票类型</p>
                        <p className="text-sm font-medium text-teal-700">{delivery.invoice.type}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <InfoRow label="发票号" value={delivery.invoice.no} />
                    <InfoRow label="开票日期" value={delivery.invoice.at} />
                  </div>

                  {delivery.invoice.file && (
                    <button className="mt-3 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700">
                      <Download size={14} />
                      下载发票文件
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm">
                  等待发票开具
                </div>
              )}
            </StageCard>

            {/* 付款完成 */}
            <StageCard
              title="付款完成"
              icon={CreditCard}
              stageData={delivery.payment}
              onClick={() => onViewPayment?.(delivery)}
              clickable={!!onViewPayment}
            >
              {delivery.payment ? (
                <div>
                  {/* 付款金额 */}
                  <div className="mb-3 p-2 bg-green-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-[10px] text-green-500">付款金额</p>
                        <p className="text-lg font-bold text-green-700">
                          ¥{(delivery.payment.amount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-green-500">付款时间</p>
                        <p className="text-sm font-medium text-green-700">{delivery.payment.at}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <InfoRow label="付款单号" value={delivery.payment.no} />
                    <InfoRow label="付款方式" value={delivery.payment.method} />
                    <InfoRow label="交易流水号" value={delivery.payment.transactionNo} />
                  </div>

                  {delivery.payment.voucher && (
                    <div className="mt-3 pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">付款凭证</p>
                      <p className="text-sm text-slate-700">{delivery.payment.voucher.no}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm">
                  等待付款
                </div>
              )}
            </StageCard>
          </div>
        </div>

        {/* 右侧 - 时间线和操作 */}
        <div className="space-y-6">
          {/* 操作按钮 */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-medium text-slate-700 mb-4">操作</h3>

            {(delivery.status === DELIVERY_STATUS.ARRIVED || delivery.status === DELIVERY_STATUS.WAITING_APPROVAL) && (
              <div className="space-y-3">
                {delivery.status === DELIVERY_STATUS.WAITING_APPROVAL ? (
                  <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                    <p>订单审批通过后可进行入库操作</p>
                  </div>
                ) : (
                  <button
                    onClick={() => onStore?.(delivery)}
                    className="w-full py-2.5 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors"
                  >
                    入库过磅
                  </button>
                )}
              </div>
            )}

            {delivery.status === DELIVERY_STATUS.PENDING_SETTLEMENT && (
              <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-700">
                <p>等待供应商确认结算金额</p>
              </div>
            )}

            {delivery.status === DELIVERY_STATUS.PAID && (
              <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700 flex items-center gap-2">
                <CheckCircle size={16} />
                <span>该送货单已完成全部流程</span>
              </div>
            )}
          </div>

          {/* 时间线 */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-medium text-slate-700 mb-4">处理时间线</h3>

            <div className="space-y-4">
              {delivery.timeline?.map((item, index) => {
                const dotColor = TIMELINE_COLORS[item.status] || 'bg-gray-400';
                const isLast = index === delivery.timeline.length - 1;

                return (
                  <div key={index} className="relative pl-6">
                    {/* 连接线 */}
                    {!isLast && (
                      <div className="absolute left-[7px] top-3 bottom-0 w-0.5 bg-slate-200 -mb-4" />
                    )}

                    {/* 节点 */}
                    <div className={`absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full ${dotColor} border-2 border-white shadow-sm`} />

                    {/* 内容 */}
                    <div className={`pb-4 ${isLast ? '' : 'border-b border-slate-100'}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-800">{item.action}</span>
                        <span className="text-xs text-slate-500">{item.time}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {DELIVERY_STATUS_LABELS[item.status]}
                        {item.operator && <span className="ml-2">· {item.operator}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
